<template>
  <div :class="['video-player-container', containerClass]" :style="containerStyle">
    <div class="video-aspect-ratio-wrapper" :style="{ paddingBottom: aspectRatioPadding }">
      <video ref="videoElement" class="video-js vjs-default-skin" :poster="placeholderImgSrc" playsinline webkit-playsinline controls>
        <source :src="actualVideoSrc" :type="videoType" />
        {{ $t('home.video_not_supported') }}
      </video>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="video-loading">
      <div class="loading-spinner"></div>
      <span>视频加载中...</span>
    </div>

    <!-- 错误状态 -->
    <div v-if="error" class="video-error">
      <p>{{ errorMessage || '视频加载失败' }}</p>
      <button @click="retryLoad" class="retry-btn">重试</button>
    </div>
  </div>
</template>

<script setup>
  import videojs from 'video.js'
  import 'video.js/dist/video-js.css'
  import { ref, onMounted, onUnmounted, watch, computed } from 'vue'

  const props = defineProps({
    src: {
      type: [String, Array],
      required: true
    },
    placeholderImgSrc: {
      type: String,
      default: ''
    },
    containerClass: {
      type: String,
      default: ''
    },
    options: {
      type: Object,
      default: () => ({})
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    muted: {
      type: Boolean,
      default: false // 改为false，避免静音问题
    },
    aspectRatio: {
      type: String,
      default: '16:9'
    },
    // 默认音量 (0-1)
    defaultVolume: {
      type: Number,
      default: 0.5,
      validator: value => value >= 0 && value <= 1
    }
  })

  const emit = defineEmits(['ready', 'play', 'pause', 'ended', 'error', 'loading'])

  // 响应式数据
  const videoElement = ref(null)
  const player = ref(null)
  const loading = ref(false)
  const error = ref(false)
  const errorMessage = ref('')
  const currentVolume = ref(props.defaultVolume)

  // 计算属性
  const containerStyle = computed(() => ({
    width: '100%',
    maxWidth: '100%'
  }))

  const aspectRatioPadding = computed(() => {
    const [width, height] = props.aspectRatio.split(':').map(Number)
    return width && height ? `${(height / width) * 100}%` : '56.25%'
  })

  // 获取实际视频源（兼容数组和字符串）
  const actualVideoSrc = computed(() => {
    return Array.isArray(props.src) ? props.src[0] : props.src
  })

  // 获取视频类型
  const videoType = computed(() => {
    const src = actualVideoSrc.value
    if (!src) return 'video/mp4'

    const extension = src.split('.').pop().toLowerCase()
    const typeMap = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg'
    }
    return typeMap[extension] || 'video/mp4'
  })

  // 最小化配置 - 接近原生video元素
  const defaultOptions = {
    controls: true,
    preload: 'auto',
    fluid: true,
    liveui: true,
    html5: {
      vhs: {
        overrideNative: false, // 不覆盖原生行为
        enableLowInitialPlaylist: true
      },
      nativeAudioTracks: false,
      nativeVideoTracks: false
    }
  }

  // 设置音量
  const setVolume = volume => {
    if (!player.value) return

    const validVolume = Math.max(0, Math.min(1, volume))
    currentVolume.value = validVolume
    player.value.volume(validVolume)

    // 如果音量设为0，自动静音；如果从0增加，取消静音
    if (validVolume === 0) {
      player.value.muted(true)
    } else if (validVolume > 0) {
      player.value.muted(false)
    }
  }
  // 初始化播放器
  const initializePlayer = () => {
    if (!videoElement.value) return

    loading.value = true
    error.value = false

    try {
      // 合并配置，保持最小化
      const playerOptions = {
        ...defaultOptions,
        ...props.options
      }

      // console.log('初始化Video.js播放器', playerOptions)

      // 创建播放器实例
      player.value = videojs(videoElement.value, playerOptions, () => {
        // console.log('Video.js播放器初始化完成')
        loading.value = false
        emit('ready', player.value)

        // 设置默认音量
        setVolume(props.defaultVolume)

        // 绑定事件
        bindPlayerEvents()

        // 手动触发一次播放测试（可选）
        setTimeout(() => {
          testPlayback()
        }, 1000)
      })
    } catch (err) {
      console.error('播放器初始化失败:', err)
      handleError(err)
    }
  }

  // 测试播放功能
  const testPlayback = async () => {
    if (!player.value) return

    try {
      // 如果是自动播放，尝试播放
      if (props.autoplay) {
        await player.value.play()
        // console.log('自动播放成功')
      }
    } catch (playErr) {
      // console.log('自动播放被阻止（正常）:', playErr.message)
      // 自动播放被阻止是正常现象，不视为错误
    }
  }

  // 绑定播放器事件
  const bindPlayerEvents = () => {
    if (!player.value) return

    const events = {
      play: () => {
        // console.log('视频开始播放')
        emit('play')
      },
      pause: () => emit('pause'),
      ended: () => emit('ended'),
      error: err => {
        console.error('视频播放错误:', err)
        handleError(err)
      },
      loadstart: () => {
        loading.value = true
        emit('loading', true)
      },
      loadeddata: () => {
        loading.value = false
        emit('loading', false)
      },
      waiting: () => {
        loading.value = true
      },
      canplay: () => {
        loading.value = false
      }
    }

    Object.entries(events).forEach(([event, handler]) => {
      player.value.on(event, handler)
    })
  }

  // 处理错误
  const handleError = err => {
    loading.value = false
    error.value = true
    errorMessage.value = err.message || '视频加载失败'
    emit('error', err)
  }

  // 重试加载
  const retryLoad = () => {
    error.value = false
    errorMessage.value = ''
    initializePlayer()
  }

  // 清理播放器
  const cleanupPlayer = () => {
    if (player.value && !player.value.isDisposed()) {
      player.value.dispose()
      player.value = null
    }
  }

  // 生命周期
  onMounted(() => {
    // 延迟初始化，确保DOM完全渲染
    setTimeout(() => {
      initializePlayer()
    }, 100)
  })

  onUnmounted(() => {
    // 设置音量
    const setVolume = volume => {
      if (!player.value) return

      const validVolume = Math.max(0, Math.min(1, volume))
      currentVolume.value = validVolume
      player.value.volume(validVolume)

      // 如果音量设为0，自动静音；如果从0增加，取消静音
      if (validVolume === 0) {
        player.value.muted(true)
        isMuted.value = true
      } else if (isMuted.value && validVolume > 0) {
        player.value.muted(false)
        isMuted.value = false
      }
    }
    cleanupPlayer()
  })

  // 监听Props变化
  watch(
    () => props.src,
    () => {
      if (player.value) {
        player.value.src(actualVideoSrc.value)
      }
    }
  )

  watch(
    () => props.defaultVolume,
    newVolume => {
      setVolume(newVolume)
    }
  )

  // 暴露方法
  defineExpose({
    play: () => player.value?.play(),
    pause: () => player.value?.pause(),
    getPlayer: () => player.value,
    isPlaying: () => player.value && !player.value.paused()
  })
</script>

<style scoped>
.video-player-container {
  position: relative;
  background: #000;
  overflow: hidden;
  width: 100%;
}

.video-aspect-ratio-wrapper {
  position: relative;
  width: 100%;
  height: 0;
}

.video-aspect-ratio-wrapper :deep(.video-js) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* 关键：让封面图充满容器并保持比例 */
.video-aspect-ratio-wrapper :deep(.video-js .vjs-poster) {
  background-size: cover !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
}

/* 或者使用 object-fit 方式 */
.video-aspect-ratio-wrapper :deep(.video-js .vjs-poster img) {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.video-loading,
.video-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  text-align: center;
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.retry-btn {
  background: #ff4757;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.retry-btn:hover {
  background: #ff3742;
}
</style>
